// src/pages/monitoring/submissions/FieldSubmissionDetail.jsx
/**
 * Field Submission Detail — v2 RBAC (FIXED)
 *
 * FIXES:
 *   1. Semua milestone tampil di tabs (bukan hanya yang active/pending)
 *      → Approved milestones tampil dengan style abu-abu/muted
 *      → Auto-select: milestone dengan pending diprioritaskan
 *
 *   2. Override detail tampil di history untuk SEMUA role:
 *      → Submitted: 74% → Approved: 60% (override) dengan alasan
 *      → Supervisor bisa lihat hasil review
 *      → PC/PM bisa lihat aksi yang sudah mereka lakukan
 *
 *   3. Progress bar di history menggunakan approvedProgress bila ada override
 *      → Lebih akurat menampilkan nilai final yang ditetapkan
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  ArrowLeft, CheckCircle, RotateCcw, ZoomIn, X, AlertTriangle,
  FileText, Camera, Clock, Info, TrendingUp, Upload,
} from 'lucide-react';
import toast from 'react-hot-toast';

import HasRole from '../../../components/auth/HasRole';

import {
  getAllSubmissionsBySegment,
  approveProgress,
  ProgressStatus,
  PENDING_STATUSES,
  formatDateTime,
} from '../../../services/progressService';
import {
  getEvidenceByMilestone,
  approveEvidence,
  rejectEvidence,
  bulkApproveEvidence,
  groupByDefinition,
  computeGroupSummary,
  computeEvidenceSummary,
  EvidenceApprovalStatus,
} from '../../../services/evidenceWebService';

import SubmitProgressModal from './SubmitProgressModal';
import UploadEvidenceModal from './UploadEvidenceModal';

// ============================================================================
// HELPERS
// ============================================================================

const Badge = ({ children, variant = 'default' }) => {
  const cls = {
    pending:   'bg-amber-100 text-amber-700 border-amber-200',
    approved:  'bg-emerald-100 text-emerald-700 border-emerald-200',
    rejected:  'bg-red-100 text-red-700 border-red-200',
    overridden:'bg-purple-100 text-purple-700 border-purple-200',
    default:   'bg-gray-100 text-gray-600 border-gray-200',
  }[variant];
  return (
    <span className={`text-xs px-2 py-0.5 rounded-full border font-medium ${cls}`}>
      {children}
    </span>
  );
};

const statusVariant = (status) =>
  PENDING_STATUSES.includes(status) ? 'pending'
  : status === ProgressStatus.APPROVED ? 'approved'
  : status === ProgressStatus.REJECTED ? 'rejected'
  : 'default';

// ============================================================================
// PROGRESS PANEL (left)
// ============================================================================

const ProgressPanel = ({ submissions, onRefresh }) => {
  const [acceptModal,   setAcceptModal]   = useState(null);
  const [overrideModal, setOverrideModal] = useState(null);
  const [overrideVal,   setOverrideVal]   = useState(0);
  const [overrideNote,  setOverrideNote]  = useState('');
  const [acceptNote,    setAcceptNote]    = useState('');
  const [saving,        setSaving]        = useState(false);

  const pending = submissions.filter(s => PENDING_STATUSES.includes(s.status));
  const history = submissions.filter(s => !PENDING_STATUSES.includes(s.status));

  const handleAccept = async () => {
    if (!acceptModal) return;
    setSaving(true);
    try {
      await approveProgress(acceptModal.progressId, {
        approveAsIs:   true,
        approvalNotes: acceptNote,
      });
      toast.success('Progress diterima');
      setAcceptModal(null);
      setAcceptNote('');
      onRefresh();
    } catch { toast.error('Gagal menyetujui progress'); }
    finally  { setSaving(false); }
  };

  const handleOverride = async () => {
    if (!overrideModal || !overrideNote.trim()) return;
    setSaving(true);
    try {
      await approveProgress(overrideModal.progressId, {
        approveAsIs:      false,
        adjustedProgress: overrideVal,
        adjustmentReason: overrideNote,
      });
      toast.success(`Progress di-override ke ${overrideVal}%`);
      setOverrideModal(null);
      setOverrideNote('');
      onRefresh();
    } catch { toast.error('Gagal override progress'); }
    finally  { setSaving(false); }
  };

  return (
    <div className="flex flex-col h-full">
      <div className="flex items-center justify-between px-4 py-3 border-b
        border-gray-100 flex-shrink-0">
        <div className="flex items-center gap-2">
          <TrendingUp className="w-4 h-4 text-blue-600" />
          <span className="text-sm font-medium text-gray-700">Progress submissions</span>
        </div>
        {pending.length > 0 && <Badge variant="pending">{pending.length} pending</Badge>}
      </div>

      <div className="flex-1 overflow-y-auto p-4 space-y-3">

        {/* ── PENDING SUBMISSIONS ─────────────────────────────── */}
        {pending.map(sub => (
          <div key={sub.progressId}
            className="border border-amber-200 rounded-xl overflow-hidden bg-amber-50/30">
            <div className="px-3 py-2.5 bg-amber-50 border-b border-amber-100
              flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-7 h-7 rounded-full bg-amber-600 flex items-center
                  justify-center text-white text-xs font-bold flex-shrink-0">
                  {(sub.submittedByName ?? 'U')[0].toUpperCase()}
                </div>
                <div>
                  <p className="text-xs font-semibold text-gray-800">{sub.submittedByName}</p>
                  <p className="text-xs text-amber-700">{formatDateTime(sub.submittedAt)}</p>
                </div>
              </div>
              <Badge variant="pending">pending</Badge>
            </div>

            <div className="px-3 py-3 space-y-2">
              <div className="flex items-center gap-2">
                <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
                  <div className="h-full bg-blue-500 rounded-full"
                    style={{ width: `${sub.submittedProgress ?? 0}%` }} />
                </div>
                <span className="text-base font-bold text-blue-700 min-w-[3rem] text-right">
                  {sub.submittedProgress ?? 0}%
                </span>
              </div>
              {sub.workDescription && (
                <p className="text-xs text-gray-600 bg-gray-50 rounded-lg px-2 py-1.5
                  border border-gray-100 italic">
                  "{sub.workDescription}"
                </p>
              )}

              {/* Manager: action buttons */}
              <HasRole isManager={true}>
                <div className="flex gap-2 pt-1">
                  <button
                    onClick={() => setAcceptModal({
                      progressId:        sub.progressId,
                      submittedProgress: sub.submittedProgress,
                    })}
                    className="flex-1 py-2 bg-emerald-50 hover:bg-emerald-100
                      text-emerald-700 text-xs font-semibold rounded-lg border
                      border-emerald-200 transition-colors flex items-center justify-center gap-1">
                    <CheckCircle className="w-3.5 h-3.5" />
                    Terima {sub.submittedProgress}%
                  </button>
                  <button
                    onClick={() => {
                      setOverrideModal({
                        progressId:        sub.progressId,
                        submittedProgress: sub.submittedProgress ?? 0,
                      });
                      setOverrideVal(sub.submittedProgress ?? 0);
                      setOverrideNote('');
                    }}
                    className="flex-1 py-2 bg-purple-50 hover:bg-purple-100
                      text-purple-700 text-xs font-semibold rounded-lg border
                      border-purple-200 transition-colors flex items-center justify-center gap-1">
                    <RotateCcw className="w-3.5 h-3.5" />
                    Override
                  </button>
                </div>
              </HasRole>

              {/* Supervisor: waiting label */}
              <HasRole isFieldWorker={true}>
                <div className="pt-1">
                  <span className="text-xs text-amber-600 bg-amber-50 border border-amber-200
                    rounded-lg px-2 py-1">
                    Menunggu review PM/PC
                  </span>
                </div>
              </HasRole>
            </div>
          </div>
        ))}

        {/* ── HISTORY (APPROVED / REJECTED) ──────────────────── */}
        {history.map(sub => {
          const isOverridden = sub.isProgressAdjusted === true
            || sub.isProgressAdjusted === 'true'
            || (sub.approvedProgress != null
                && sub.approvedProgress !== sub.submittedProgress);
          const finalValue   = isOverridden ? sub.approvedProgress : sub.approvedProgress ?? sub.submittedProgress;
          const isApproved   = sub.status === ProgressStatus.APPROVED;
          const isRejected   = sub.status === ProgressStatus.REJECTED;

          return (
            <div key={sub.progressId}
              className={`border rounded-xl overflow-hidden ${
                isApproved && isOverridden ? 'border-purple-200'
                : isApproved ? 'border-emerald-200'
                : isRejected ? 'border-red-200'
                : 'border-gray-200'}`}>

              {/* Row header */}
              <div className={`px-3 py-2.5 flex items-center justify-between border-b ${
                isApproved && isOverridden ? 'bg-purple-50 border-purple-100'
                : isApproved ? 'bg-emerald-50 border-emerald-100'
                : isRejected ? 'bg-red-50 border-red-100'
                : 'bg-gray-50 border-gray-100'}`}>
                <div className="flex items-center gap-2">
                  <div className={`w-6 h-6 rounded-full flex items-center justify-center
                    text-white text-xs font-bold flex-shrink-0 ${
                    isApproved && isOverridden ? 'bg-purple-600'
                    : isApproved ? 'bg-emerald-600'
                    : isRejected ? 'bg-red-600'
                    : 'bg-gray-500'}`}>
                    {(sub.submittedByName ?? 'U')[0].toUpperCase()}
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-gray-800">{sub.submittedByName}</p>
                    <p className="text-xs text-gray-500">{formatDateTime(sub.submittedAt)}</p>
                  </div>
                </div>
                <Badge variant={isApproved && isOverridden ? 'overridden' : statusVariant(sub.status)}>
                  {isApproved && isOverridden ? 'di-override'
                   : isApproved ? 'approved'
                   : isRejected ? 'ditolak'
                   : sub.status}
                </Badge>
              </div>

              {/* Progress detail */}
              <div className="px-3 py-3 space-y-2">
                {/* ── Submitted value ── */}
                <div className="flex items-center gap-1.5 text-xs text-gray-500">
                  <span className="font-medium">Submitted:</span>
                  <span className="font-bold text-gray-700">{sub.submittedProgress ?? 0}%</span>
                </div>

                {/* ── Override info ── */}
                {isOverridden && (
                  <div className="bg-purple-50 border border-purple-100 rounded-lg px-2.5 py-2 space-y-1">
                    <div className="flex items-center gap-1.5 text-xs">
                      <RotateCcw className="w-3 h-3 text-purple-600 flex-shrink-0" />
                      <span className="text-purple-600 font-medium">
                        Di-override: {sub.submittedProgress}% → {sub.approvedProgress}%
                      </span>
                    </div>
                    {sub.adjustmentReason && (
                      <p className="text-xs text-purple-700 italic pl-4.5">
                        "{sub.adjustmentReason}"
                      </p>
                    )}
                    {sub.approvedBy && (
                      <p className="text-xs text-purple-500 pl-4.5">
                        oleh {sub.approvedBy} · {formatDateTime(sub.approvedAt)}
                      </p>
                    )}
                  </div>
                )}

                {/* ── Accept info (no override) ── */}
                {isApproved && !isOverridden && (
                  <div className="bg-emerald-50 border border-emerald-100 rounded-lg px-2.5 py-2 space-y-1">
                    <div className="flex items-center gap-1.5 text-xs">
                      <CheckCircle className="w-3 h-3 text-emerald-600 flex-shrink-0" />
                      <span className="text-emerald-600 font-medium">
                        Diterima: {finalValue}%
                      </span>
                    </div>
                    {sub.approvalNotes && (
                      <p className="text-xs text-emerald-700 italic pl-4.5">
                        "{sub.approvalNotes}"
                      </p>
                    )}
                    {sub.approvedBy && (
                      <p className="text-xs text-emerald-500 pl-4.5">
                        oleh {sub.approvedBy} · {formatDateTime(sub.approvedAt)}
                      </p>
                    )}
                  </div>
                )}

                {/* ── Rejection info ── */}
                {isRejected && sub.rejectionReason && (
                  <div className="bg-red-50 border border-red-100 rounded-lg px-2.5 py-2">
                    <div className="flex items-start gap-1.5 text-xs">
                      <AlertTriangle className="w-3 h-3 text-red-600 flex-shrink-0 mt-0.5" />
                      <div>
                        <span className="text-red-600 font-medium">Ditolak: </span>
                        <span className="text-red-700 italic">"{sub.rejectionReason}"</span>
                      </div>
                    </div>
                  </div>
                )}

                {/* ── Final progress bar ── */}
                {isApproved && (
                  <div className="pt-1">
                    <div className="flex items-center gap-1.5 mb-1">
                      <span className="text-xs text-gray-400">Progress final:</span>
                      <span className={`text-sm font-bold ${
                        isOverridden ? 'text-purple-700' : 'text-emerald-700'}`}>
                        {finalValue}%
                      </span>
                    </div>
                    <div className="h-1.5 bg-gray-200 rounded-full overflow-hidden">
                      <div
                        className={`h-full rounded-full ${
                          isOverridden ? 'bg-purple-500' : 'bg-emerald-500'}`}
                        style={{ width: `${finalValue ?? 0}%` }} />
                    </div>
                  </div>
                )}

                {/* Notes from supervisor */}
                {sub.workDescription && (
                  <p className="text-xs text-gray-500 italic">
                    "{sub.workDescription}"
                  </p>
                )}
              </div>
            </div>
          );
        })}

        {submissions.length === 0 && (
          <p className="text-xs text-gray-400 text-center py-8">
            Belum ada progress submission untuk milestone ini.
          </p>
        )}
      </div>

      {/* ACCEPT MODAL */}
      {acceptModal && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl border border-gray-200 w-full max-w-sm">
            <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
              <span className="font-medium text-sm">Terima Progress</span>
              <button onClick={() => setAcceptModal(null)}>
                <X className="w-4 h-4 text-gray-400" />
              </button>
            </div>
            <div className="p-4 space-y-3">
              <p className="text-sm text-gray-600">
                Progress <strong>{acceptModal.submittedProgress}%</strong> akan diterima
                sebagai progress resmi milestone ini.
              </p>
              <textarea rows={2} value={acceptNote}
                onChange={e => setAcceptNote(e.target.value)}
                placeholder="Catatan untuk supervisor (opsional)..."
                className="w-full border border-gray-200 rounded-lg p-2 text-sm
                  focus:outline-none focus:ring-1 focus:ring-emerald-400 resize-none" />
            </div>
            <div className="px-4 py-3 border-t border-gray-100 flex justify-end gap-2">
              <button onClick={() => setAcceptModal(null)}
                className="px-3 py-1.5 text-sm border border-gray-200 rounded-lg hover:bg-gray-50">
                Batal
              </button>
              <button onClick={handleAccept} disabled={saving}
                className="px-4 py-1.5 text-sm bg-emerald-600 hover:bg-emerald-700
                  text-white rounded-lg font-medium disabled:opacity-50">
                {saving ? 'Menyimpan...' : 'Terima Progress'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* OVERRIDE MODAL */}
      {overrideModal && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl border border-gray-200 w-full max-w-sm">
            <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
              <span className="font-medium text-sm">Override Progress</span>
              <button onClick={() => setOverrideModal(null)}>
                <X className="w-4 h-4 text-gray-400" />
              </button>
            </div>
            <div className="p-4 space-y-3">
              <div className="bg-blue-50 border border-blue-100 rounded-lg p-2.5 text-xs text-blue-700">
                Supervisor submit: <strong>{overrideModal.submittedProgress}%</strong>
              </div>
              <div>
                <div className="flex justify-between text-xs mb-1">
                  <span className="text-gray-500">Nilai yang ditetapkan</span>
                  <span className="font-bold text-purple-700">{overrideVal}%</span>
                </div>
                <input type="range" min={0} max={100} step={1} value={overrideVal}
                  onChange={e => setOverrideVal(Number(e.target.value))}
                  className="w-full accent-purple-600" />
                {overrideVal !== overrideModal.submittedProgress && (
                  <p className="text-xs text-amber-600 mt-1 flex items-center gap-1">
                    <Info className="w-3 h-3" />
                    {overrideModal.submittedProgress}% → {overrideVal}%
                  </p>
                )}
              </div>
              <div>
                <label className="text-xs font-medium text-gray-500 block mb-1">
                  Alasan override <span className="text-red-500">*</span>
                </label>
                <textarea rows={3} value={overrideNote}
                  onChange={e => setOverrideNote(e.target.value)}
                  placeholder="Jelaskan alasan penyesuaian nilai progress..."
                  className="w-full border border-gray-200 rounded-lg p-2 text-sm
                    focus:outline-none focus:ring-1 focus:ring-purple-400 resize-none" />
              </div>
            </div>
            <div className="px-4 py-3 border-t border-gray-100 flex justify-end gap-2">
              <button onClick={() => setOverrideModal(null)}
                className="px-3 py-1.5 text-sm border border-gray-200 rounded-lg hover:bg-gray-50">
                Batal
              </button>
              <button onClick={handleOverride}
                disabled={saving || !overrideNote.trim()}
                className="px-4 py-1.5 text-sm bg-purple-600 hover:bg-purple-700
                  text-white rounded-lg font-medium disabled:opacity-50">
                {saving ? 'Menyimpan...' : 'Simpan Override'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ============================================================================
// EVIDENCE PANEL (right) — tidak berubah dari versi sebelumnya
// salin dari FieldSubmissionDetail.jsx eksisting
// ============================================================================

const EvidencePanel = ({ milestoneId, onRefresh }) => {
  const [evidence,     setEvidence]     = useState([]);
  const [loading,      setLoading]      = useState(false);
  const [rejectModal,  setRejectModal]  = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [saving,       setSaving]       = useState(false);
  const [lightbox,     setLightbox]     = useState(null);

  const load = useCallback(async () => {
    if (!milestoneId) return;
    setLoading(true);
    try {
      const data = await getEvidenceByMilestone(milestoneId);
      setEvidence(data);
    } catch { /* silent */ }
    finally { setLoading(false); }
  }, [milestoneId]);

  useEffect(() => { load(); }, [load]);

  const groups  = useMemo(() => groupByDefinition(evidence), [evidence]);
  const summary = useMemo(() => computeEvidenceSummary(evidence), [evidence]);

  const handleApproveFile = async (evidenceId) => {
    setSaving(true);
    try {
      await approveEvidence(evidenceId);
      toast.success('Evidence disetujui');
      load(); onRefresh();
    } catch { toast.error('Gagal menyetujui evidence'); }
    finally { setSaving(false); }
  };

  const handleApproveAll = async () => {
    const ids = evidence
      .filter(e => e.approvalStatus === EvidenceApprovalStatus.PENDING)
      .map(e => e.id);
    if (!ids.length) return;
    setSaving(true);
    try {
      await bulkApproveEvidence(ids);
      toast.success(`${ids.length} evidence disetujui`);
      load(); onRefresh();
    } catch { toast.error('Gagal bulk approve'); }
    finally { setSaving(false); }
  };

  const handleRejectFile = async () => {
    if (!rejectModal || !rejectReason.trim()) return;
    setSaving(true);
    try {
      await rejectEvidence(rejectModal.evidenceId, rejectReason);
      toast.success('Evidence ditolak');
      setRejectModal(null); setRejectReason('');
      load(); onRefresh();
    } catch { toast.error('Gagal menolak evidence'); }
    finally { setSaving(false); }
  };

  return (
    <div className="flex flex-col h-full">
      <div className="flex items-center justify-between px-4 py-3 border-b
        border-gray-100 flex-shrink-0">
        <div className="flex items-center gap-2">
          <Camera className="w-4 h-4 text-purple-600" />
          <span className="text-sm font-medium text-gray-700">Evidence submissions</span>
        </div>
        <div className="flex gap-2 items-center">
          {summary.hasRejected && <Badge variant="rejected">ada yang ditolak</Badge>}
          {summary.pending > 0  && <Badge variant="pending">{summary.pending} pending</Badge>}
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-4 space-y-3">
        <HasRole isManager={true}>
          {summary.pending > 0 && (
            <button onClick={handleApproveAll} disabled={saving}
              className="w-full py-2 bg-emerald-50 hover:bg-emerald-100
                text-emerald-700 text-sm font-semibold rounded-lg border
                border-emerald-200 flex items-center justify-center gap-2">
              <CheckCircle className="w-4 h-4" />
              Approve semua pending ({summary.pending})
            </button>
          )}
        </HasRole>

        {groups.map(group => {
          const gs = computeGroupSummary(group);
          return (
            <div key={group.definitionId ?? 'general'}
              className={`border rounded-xl overflow-hidden ${
                gs.hasRejected ? 'border-red-200' :
                gs.allApproved ? 'border-emerald-200' : 'border-gray-200'}`}>
              <div className={`px-3 py-2 flex items-center justify-between border-b ${
                gs.hasRejected ? 'bg-red-50 border-red-100' :
                gs.allApproved ? 'bg-emerald-50 border-emerald-100' : 'bg-gray-50 border-gray-100'}`}>
                <div>
                  <div className="flex items-center gap-1.5">
                    <span className={`text-xs px-1.5 py-0.5 rounded font-semibold ${
                      group.evidenceType === 'PHOTO'
                        ? 'bg-blue-100 text-blue-700' : 'bg-purple-100 text-purple-700'}`}>
                      {group.evidenceType === 'PHOTO' ? 'FOTO' : 'DOK'}
                    </span>
                    <span className={`text-xs px-1.5 py-0.5 rounded font-semibold ${
                      group.isMandatory
                        ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-500'}`}>
                      {group.isMandatory ? 'Wajib' : 'Opsional'}
                    </span>
                  </div>
                  <p className="text-sm font-semibold text-gray-800 mt-1">
                    {group.definitionName}
                  </p>
                </div>
                <span className="text-xs text-gray-400">{group.files.length} file</span>
              </div>

              <div className="p-3">
                {group.evidenceType === 'PHOTO' ? (
                  <div className="grid grid-cols-3 gap-2">
                    {group.files.map(file => (
                      <div key={file.id}
                        className={`rounded-lg overflow-hidden border-2 ${
                          file.approvalStatus === 'APPROVED' ? 'border-emerald-400' :
                          file.approvalStatus === 'REJECTED' ? 'border-red-400' :
                          'border-gray-200'}`}>
                        <div
                          className="relative aspect-video flex items-center justify-center
                            cursor-pointer group bg-gray-100"
                          onClick={() => setLightbox({ url: file.storageUrl, name: file.originalFilename })}>
                          {file.thumbnailUrl ? (
                            <img src={file.thumbnailUrl} alt={file.originalFilename}
                              className="w-full h-full object-cover" />
                          ) : (
                            <Camera className="w-6 h-6 text-gray-300" />
                          )}
                          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20
                            flex items-center justify-center transition-all">
                            <ZoomIn className="w-4 h-4 text-white opacity-0
                              group-hover:opacity-100 transition-opacity" />
                          </div>
                          <div className={`absolute top-1 left-1 w-4 h-4 rounded
                            flex items-center justify-center text-white text-xs ${
                            file.approvalStatus === 'APPROVED' ? 'bg-emerald-500' :
                            file.approvalStatus === 'REJECTED' ? 'bg-red-500' : 'bg-amber-400'}`}>
                            {file.approvalStatus === 'APPROVED' ? '✓' :
                             file.approvalStatus === 'REJECTED' ? '✕' : '·'}
                          </div>
                        </div>
                        {file.approvalStatus === 'REJECTED' && file.rejectionReason && (
                          <p className="px-1.5 py-1 text-xs text-red-600 bg-red-50
                            border-t border-red-100 line-clamp-2">
                            {file.rejectionReason}
                          </p>
                        )}
                        <HasRole isManager={true}>
                          {file.approvalStatus === 'PENDING' && (
                            <div className="flex border-t border-gray-100">
                              <button onClick={() => handleApproveFile(file.id)} disabled={saving}
                                className="flex-1 py-1 text-xs font-semibold text-emerald-700
                                  bg-emerald-50 hover:bg-emerald-100 border-r border-gray-100">
                                OK
                              </button>
                              <button
                                onClick={() => { setRejectModal({ evidenceId: file.id }); setRejectReason(''); }}
                                className="flex-1 py-1 text-xs font-semibold text-red-600
                                  bg-red-50 hover:bg-red-100">
                                Tolak
                              </button>
                            </div>
                          )}
                        </HasRole>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="space-y-2">
                    {group.files.map(file => (
                      <div key={file.id}
                        className={`flex items-center gap-3 p-2.5 rounded-lg border ${
                          file.approvalStatus === 'APPROVED' ? 'border-emerald-200 bg-emerald-50/30' :
                          file.approvalStatus === 'REJECTED' ? 'border-red-200 bg-red-50/30' :
                          'border-gray-200 bg-gray-50'}`}>
                        <div className={`w-8 h-8 rounded-lg flex items-center
                          justify-center flex-shrink-0 ${
                          file.approvalStatus === 'APPROVED' ? 'bg-emerald-100' :
                          file.approvalStatus === 'REJECTED' ? 'bg-red-100' : 'bg-blue-100'}`}>
                          <FileText className={`w-4 h-4 ${
                            file.approvalStatus === 'APPROVED' ? 'text-emerald-600' :
                            file.approvalStatus === 'REJECTED' ? 'text-red-600' : 'text-blue-600'}`} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-medium text-gray-700 truncate">
                            {file.originalFilename}
                          </p>
                          {file.rejectionReason && (
                            <p className="text-xs text-red-600 mt-0.5">{file.rejectionReason}</p>
                          )}
                        </div>
                        <div className="flex items-center gap-1 flex-shrink-0">
                          <a href={file.storageUrl} target="_blank" rel="noopener noreferrer"
                            className="text-xs px-2 py-1 bg-blue-600 text-white rounded">
                            View
                          </a>
                          <HasRole isManager={true}>
                            {file.approvalStatus === 'PENDING' && (
                              <>
                                <button onClick={() => handleApproveFile(file.id)} disabled={saving}
                                  className="text-xs px-2 py-1 bg-emerald-50 text-emerald-700
                                    border border-emerald-200 rounded font-medium">
                                  OK
                                </button>
                                <button
                                  onClick={() => { setRejectModal({ evidenceId: file.id }); setRejectReason(''); }}
                                  className="text-xs px-2 py-1 bg-red-50 text-red-600
                                    border border-red-200 rounded font-medium">
                                  Tolak
                                </button>
                              </>
                            )}
                          </HasRole>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {group.files.length === 0 && (
                  <div className="flex items-center gap-2 text-xs text-gray-400
                    py-3 justify-center border border-dashed border-gray-200 rounded-lg">
                    <Clock className="w-3.5 h-3.5" />
                    Menunggu upload dari supervisor
                  </div>
                )}
              </div>
            </div>
          );
        })}

        {loading && <p className="text-xs text-gray-400 text-center py-6">Memuat evidence...</p>}
        {!loading && groups.length === 0 && (
          <p className="text-xs text-gray-400 text-center py-8">
            Belum ada evidence untuk milestone ini.
          </p>
        )}
      </div>

      {/* REJECT MODAL */}
      {rejectModal && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl border border-gray-200 w-full max-w-sm">
            <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
              <span className="font-medium text-sm text-red-700">Tolak Evidence</span>
              <button onClick={() => setRejectModal(null)}>
                <X className="w-4 h-4 text-gray-400" />
              </button>
            </div>
            <div className="p-4 space-y-3">
              <div className="bg-red-50 border border-red-100 rounded-lg p-2.5 text-xs text-red-700">
                File ini akan ditolak. Supervisor akan diminta upload ulang.
              </div>
              <div>
                <label className="text-xs font-medium text-gray-500 block mb-1">
                  Alasan penolakan <span className="text-red-500">*</span>
                </label>
                <textarea rows={3} value={rejectReason}
                  onChange={e => setRejectReason(e.target.value)}
                  placeholder="Contoh: Foto terlalu gelap, tidak terlihat detail..."
                  className="w-full border border-gray-200 rounded-lg p-2 text-sm
                    focus:outline-none focus:ring-1 focus:ring-red-400 resize-none" />
              </div>
            </div>
            <div className="px-4 py-3 border-t border-gray-100 flex justify-end gap-2">
              <button onClick={() => setRejectModal(null)}
                className="px-3 py-1.5 text-sm border border-gray-200 rounded-lg hover:bg-gray-50">
                Batal
              </button>
              <button onClick={handleRejectFile}
                disabled={saving || !rejectReason.trim()}
                className="px-4 py-1.5 text-sm bg-red-600 hover:bg-red-700
                  text-white rounded-lg font-medium disabled:opacity-50">
                {saving ? 'Menyimpan...' : 'Tolak File'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* LIGHTBOX */}
      {lightbox && (
        <div className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center"
          onClick={() => setLightbox(null)}>
          <button className="absolute top-4 right-4 text-white/70 hover:text-white">
            <X className="w-8 h-8" />
          </button>
          <img src={lightbox.url} alt={lightbox.name}
            className="max-w-4xl max-h-[85vh] object-contain rounded-xl"
            onClick={e => e.stopPropagation()} />
        </div>
      )}
    </div>
  );
};

// ============================================================================
// MAIN PAGE
// ============================================================================

export default function FieldSubmissionDetail() {
  const { segmentId, siteId } = useParams();
  const navigate  = useNavigate();
  const { state } = useLocation();

  const scopeId   = segmentId ? Number(segmentId) : Number(siteId);
  const scopeType = segmentId ? 'SEGMENT' : 'SITE';
  const scopeName = state?.scopeName ?? (scopeType === 'SEGMENT' ? 'FO Segment' : 'Tower Site');

  const [submissions,       setSubmissions]       = useState([]);
  const [selectedMilestone, setSelectedMilestone] = useState(null);
  const [loading,           setLoading]           = useState(false);
  const [evidenceRefresh,   setEvidenceRefresh]   = useState(0);
  const [showProgressModal, setShowProgressModal] = useState(false);
  const [showEvidenceModal, setShowEvidenceModal] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getAllSubmissionsBySegment(scopeId);
      setSubmissions(data);

      // Auto-select priority: pending > non-approved > first
      const groups = Array.from(new Map(data.map(s => [s.milestoneId, s])).values());
      const withPending   = groups.find(s => PENDING_STATUSES.includes(s.status));
      const withNonApproved = groups.find(s => s.status !== ProgressStatus.APPROVED);
      const first         = withPending ?? withNonApproved ?? groups[0];
      if (first && !selectedMilestone) setSelectedMilestone(first.milestoneId);
    } catch {
      toast.error('Gagal memuat data submissions');
    } finally {
      setLoading(false);
    }
  }, [scopeId]);

  useEffect(() => { load(); }, [load]);

  // Build milestone groups dari semua submissions
  const milestoneGroups = useMemo(() => {
    const map = new Map();
    submissions.forEach(s => {
      if (!map.has(s.milestoneId)) {
        map.set(s.milestoneId, {
          milestoneId:       s.milestoneId,
          milestoneName:     s.milestoneName,
          milestoneItemCode: s.milestoneItemCode,
          submissions: [],
        });
      }
      map.get(s.milestoneId).submissions.push(s);
    });
    return Array.from(map.values());
  }, [submissions]);

  // FIX: Tampilkan SEMUA milestone di tabs (bukan hanya yang active)
  // Approved milestone tetap tampil dengan style berbeda
  const allMilestones  = milestoneGroups;

  const selectedGroup = milestoneGroups.find(mg => mg.milestoneId === selectedMilestone);

  const handleRefresh = () => {
    load();
    setEvidenceRefresh(r => r + 1);
  };

  return (
    <div className="min-h-full flex flex-col">

      {/* Topbar */}
      <div className="bg-white border-b border-gray-200 px-4 py-3 flex
        items-center justify-between flex-shrink-0">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate('/monitoring/submissions')}
            className="flex items-center gap-1.5 text-sm text-gray-500
              hover:text-gray-800 transition-colors">
            <ArrowLeft className="w-4 h-4" />
            Field Submissions
          </button>
          <span className="text-gray-300">/</span>
          <span className="text-sm font-medium text-gray-800 truncate max-w-xs">
            {scopeName}
          </span>
        </div>

        <div className="flex gap-2">
          <HasRole isFieldWorker={true}>
            <button onClick={() => setShowProgressModal(true)}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium
                bg-blue-50 hover:bg-blue-100 text-blue-700 border border-blue-200
                rounded-lg transition-colors">
              <TrendingUp className="w-3.5 h-3.5" />
              Submit Progress
            </button>
            <button onClick={() => setShowEvidenceModal(true)}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium
                bg-purple-50 hover:bg-purple-100 text-purple-700 border border-purple-200
                rounded-lg transition-colors">
              <Upload className="w-3.5 h-3.5" />
              Upload Evidence
            </button>
          </HasRole>
          <HasRole isManager={true}>
            <button onClick={() => setShowProgressModal(true)}
              title="Submit Progress"
              className="p-1.5 text-gray-400 hover:text-blue-600 border border-gray-200
                hover:border-blue-200 rounded-lg transition-colors bg-white">
              <TrendingUp className="w-3.5 h-3.5" />
            </button>
            <button onClick={() => setShowEvidenceModal(true)}
              title="Upload Evidence"
              className="p-1.5 text-gray-400 hover:text-purple-600 border border-gray-200
                hover:border-purple-200 rounded-lg transition-colors bg-white">
              <Upload className="w-3.5 h-3.5" />
            </button>
          </HasRole>
        </div>
      </div>

      {/* FIX: Tampilkan SEMUA milestone di tabs */}
      {allMilestones.length > 0 && (
        <div className="border-b border-gray-200 px-4 flex gap-0 overflow-x-auto
          flex-shrink-0 bg-white">
          {allMilestones.map(mg => {
            const hasPending  = mg.submissions.some(s => PENDING_STATUSES.includes(s.status));
            const allApproved = mg.submissions.every(s => s.status === ProgressStatus.APPROVED);
            const isSelected  = selectedMilestone === mg.milestoneId;

            return (
              <button
                key={mg.milestoneId}
                onClick={() => setSelectedMilestone(mg.milestoneId)}
                className={`flex items-center gap-1.5 px-4 py-3 text-sm border-b-2
                  whitespace-nowrap transition-colors ${
                  isSelected
                    ? 'border-blue-600 text-blue-700 font-medium'
                    : allApproved
                      ? 'border-transparent text-gray-400 hover:text-emerald-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700'}`}>
                <span className="text-xs text-gray-400 font-mono">
                  {mg.milestoneItemCode}
                </span>
                <span className={allApproved && !isSelected ? 'text-gray-400' : ''}>
                  {mg.milestoneName}
                </span>
                {/* Indicator dot */}
                {hasPending && (
                  <span className="w-2 h-2 rounded-full bg-amber-400 flex-shrink-0" />
                )}
                {allApproved && !hasPending && (
                  <span className="w-2 h-2 rounded-full bg-emerald-400 flex-shrink-0" />
                )}
              </button>
            );
          })}
        </div>
      )}

      {/* Two-panel content */}
      <div className="flex-1 overflow-hidden">
        {loading && submissions.length === 0 ? (
          <div className="flex items-center justify-center h-64">
            <p className="text-sm text-gray-400">Memuat data...</p>
          </div>
        ) : selectedGroup ? (
          <div className="grid grid-cols-2 h-full divide-x divide-gray-100">
            <div className="overflow-hidden flex flex-col">
              <ProgressPanel
                submissions={selectedGroup.submissions}
                onRefresh={handleRefresh}
              />
            </div>
            <div className="overflow-hidden flex flex-col">
              <EvidencePanel
                key={`${selectedGroup.milestoneId}_${evidenceRefresh}`}
                milestoneId={selectedGroup.milestoneId}
                onRefresh={handleRefresh}
              />
            </div>
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center h-64 gap-2">
            <CheckCircle className="w-10 h-10 text-emerald-300" />
            <p className="text-sm text-gray-500">
              Tidak ada submission untuk segment ini.
            </p>
            <button onClick={() => navigate('/monitoring/submissions')}
              className="text-blue-600 text-sm hover:underline">
              Kembali ke list
            </button>
          </div>
        )}
      </div>

      {/* Modals */}
      {showProgressModal && (
        <SubmitProgressModal
          onClose={() => setShowProgressModal(false)}
          onSuccess={handleRefresh}
        />
      )}
      {showEvidenceModal && (
        <UploadEvidenceModal
          onClose={() => setShowEvidenceModal(false)}
          onSuccess={handleRefresh}
        />
      )}
    </div>
  );
}